import { defineComponent as _defineComponent } from 'vue'
import {
  computed,
  useContext,
  useStore,
  useRoute,
  onMounted,
  useRouter,
  onBeforeMount,
  ref,
} from '@nuxtjs/composition-api';
import GoogleButton from './auth/GoogleButton.vue';
import FacebookButton from './auth/FacebookButton.vue';
import TDivider from '~/components/elements/t-divider.vue';
import TButton from '~/components/atoms/t-button.vue';
import TLabel from '~/components/atoms/t-label.vue';
import TInput from '~/components/atoms/t-input.vue';
import TInputGroup from '~/components/elements/t-input-group.vue';
import Recaptcha from '@/components/Recaptcha.vue';
import validateEmail from '~/utils/validateEmail';
import { isResourcePath } from '~/utils/resource';
import { ExperimentTypes } from '~/types/experiments';
import { useTrackSignup } from '~/composables/useTrackSignUp';
import { State } from '@/store';
import { getFirstItemFromQueryParam } from '~/utils/route';
import { User } from '~/types/user';
import { LoginType } from '~/types/api';

interface Props {
  modalStep?: number;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'SignUp',
  props: {
    modalStep: { default: 0 }
  },
  emits: ['increment-modal-step'],
  setup(__props: any, { emit: $emit }) {

const props = __props as { modalStep: number };

const $store = useStore<State>();
const $route = useRoute();
const $router = useRouter();

const {
  localePath,
  $config,
  $intercom,
  $intercomIdentify,
  $mzAnalyticsApi,
  $setAlgoliaUserToken,
  $isFlagOn,
  i18n,
} = useContext();

const { getPlanName } = useTrackSignup();


const responseError = ref<string>('');
const isSubmitting = ref<boolean>(false);
const email = ref<string>('');
const emailValidationMessage = ref<string>('');
const firstName = ref<string>('');
const lastName = ref<string>('');
const password = ref<string>('');
const recaptchaError = ref<boolean>(false);
// const experimentTypes = ref<ExperimentType>(ExperimentTypes);

const loggedInUser = computed(() => $store.getters.loggedInUser);
const currentPlan = computed(() => $store.getters.currentPlan);
const resource = computed(() => $store.getters.resource);

const sanitisedRedirect = computed((): string | undefined => {
  if (currentPlan.value) {
    return `/upgrade?plan=${currentPlan.value}`;
  }
  if ($route.value.query.redirect) {
    return getFirstItemFromQueryParam($route.value.query.redirect);
  } else {
    return undefined;
  }
});

const shouldShowRecaptcha = computed(() => {
  return $isFlagOn(ExperimentTypes.Recaptcha);
});

const nextPage = computed(() => {
  if (currentPlan.value) {
    return `/upgrade?plan=${currentPlan.value}`;
  }
  return localePath('/signup-complete');
});

onBeforeMount(() => {
  const { plan, referral, rsCode } = $route.value.query;
  if (plan) {
    $store.dispatch('setCurrentPlan', plan);
  } else {
    $store.dispatch('setCurrentPlan', null);
  }
  if ((referral || rsCode) && $isFlagOn('saasquatch-integration')) {
    $store.dispatch('setReferralCode', referral || rsCode);
  }
});

onMounted(() => {
  // country = location?.code || 'au';
  $mzAnalyticsApi({
    event: 'pageView',
  });
});

const resetValidationMessages = () => {
  emailValidationMessage.value = '';
  responseError.value = '';
};


const signup = async () => {
  let recaptcha = '';
  if ($isFlagOn(ExperimentTypes.Recaptcha)) {
    recaptcha = window.grecaptcha?.enterprise?.getResponse();
    if (!recaptcha) {
      recaptchaError.value = true;
      return;
    }
  }

  isSubmitting.value = true;
  let hasValidationErrors = false;
  resetValidationMessages();
  if (!validateEmail(email.value)) {
    emailValidationMessage.value = 'Please enter a valid email address';
    hasValidationErrors = true;
  }

  if (!password.value || !firstName.value || !lastName.value) {
    responseError.value = 'Please fill in all fields';
    hasValidationErrors = true;
  }

  if (hasValidationErrors) {
    isSubmitting.value = false;
    return;
  }

  try {
    const user = await $store.dispatch('freeSignUp', {
      password: password.value,
      firstName: firstName.value,
      lastName: lastName.value,
      email: email.value,
      country: i18n.locale,
      recaptcha,
    });
    trackSignUpSuccess(user, 'email');
    if (props.modalStep) {
      $emit('increment-modal-step');
    } else {
      $router.push(nextPage.value);
    }
  } catch (e: any) {
    responseError.value = e.response?.data?.error || 'Something went wrong!';
    $router.replace({ query: {} }, () => {});
  }
  isSubmitting.value = false;
};

const trackSignUpSuccess = (user: User, type: LoginType = 'email') => {
  const isResourcePage = isResourcePath($route.value.path);
  const resourceParams =
    isResourcePage && resource.value.event_params
      ? resource.value.event_params
      : {
          Page_Type_Name: 'Page',
          Page_Name: document.title,
          Page_Url: $config.baseURL + $route.value.path,
          Page_Country: i18n.locale,
        };

  if (user?.id) {
    $setAlgoliaUserToken(String(user.id));
  }

  if (typeof $intercom !== 'undefined') {
    $intercom.trackEvent('Account Created');
    $intercom.trackEvent('Logged In');
    $intercomIdentify();
  }

  if (process.client && typeof window.gtag !== 'undefined') {
    window.gtag('event', 'accountCreated', {
      userUuid: loggedInUser.value.uuid,
      eventAction: 'Account Created',
      intendedPlan: $route.value.query.plan ?? 'Free',
    });
  }
};
const trackSignUpClick = () => {
  const planName = getPlanName(currentPlan.value || 'free')
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .replace(/\s+/g, '_')
    .toLowerCase();
  $mzAnalyticsApi({
    event: 'click',
    name: `${planName}.signup`,
    productArea: 'profile',
  });
};

return { __sfc: true,$store, $route, $router, localePath, $config, $intercom, $intercomIdentify, $mzAnalyticsApi, $setAlgoliaUserToken, $isFlagOn, i18n, getPlanName, props, responseError, isSubmitting, email, emailValidationMessage, firstName, lastName, password, recaptchaError, loggedInUser, currentPlan, resource, sanitisedRedirect, shouldShowRecaptcha, nextPage, resetValidationMessages, $emit, signup, trackSignUpSuccess, trackSignUpClick, GoogleButton, FacebookButton, TDivider, TButton, TLabel, TInput, TInputGroup, Recaptcha }
}

})